import { type FunctionComponent, PropsWithChildren } from 'react';
import classes from './AppShell.module.css';
import cx from 'clsx';
import Header from '@/components/layouts/AppShell/Header';
import AiAgentIcon from '@/components/AiAgentIcon';
import UnstyledLinkButton from '@/components/mantinenext/UnstyledLinkButton';
import { useAuthentication } from '@/contexts/Authentication';
import useAppIsFramed from '@/hooks/useAppIsFramed';

type AppShellProps = PropsWithChildren & {
  withAgentButton?: boolean;
  withLoginLink?: boolean;
  withMenu?: boolean;
  withNavigation?: boolean;
  narrowContent?: boolean;
  closeHref?: string;
};

const AppShell: FunctionComponent<AppShellProps> = ({
  children,
  withAgentButton = true,
  withLoginLink = false,
  withMenu = true,
  withNavigation = true,
  narrowContent = false,
  closeHref,
}) => {
  const { authenticated } = useAuthentication();
  const showAgentButton = authenticated && withAgentButton;
  const appIsFramed = useAppIsFramed();

  const dataAttrs = {
    'data-framed': appIsFramed ? true : undefined,
    'data-with-navigation': withNavigation ? true : undefined,
    'data-narrow-content': narrowContent ? true : undefined,
  };

  return (
    <div className={cx(classes.root)} {...dataAttrs}>
      <header className={classes.header}>
        <Header
          withNavigation={withNavigation}
          withLoginLink={withLoginLink && !appIsFramed}
          withProfileDrawer={withMenu && !appIsFramed}
          closeHref={closeHref}
        />
      </header>

      {/* eslint-disable-next-line react/forbid-elements -- This is a layout, therefore
          <main> is allowed. */}
      <main className={cx(classes.content)}>{children}</main>

      {showAgentButton && (
        <UnstyledLinkButton
          className={cx(classes.button)}
          href="/agent"
          aria-label="Navigate to AI assistant">
          <AiAgentIcon alt="AI Assistant" size="lg" />
        </UnstyledLinkButton>
      )}
    </div>
  );
};

export default AppShell;
